import React, { useMemo, useRef } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import AdvancedChartLegend from '../../../../_shared/components/AdvancedChartLegend';
import Skeleton from '../../../../../../components/Skeleton';
import { SmartChart } from '../../../../../../components/Chart';
import useAdvancedLegend, {
  useHoveredItemClassName,
  useToggleSeriesVisibility,
} from '../../../../_shared/hooks/useAdvancedLegend';
import { getChartConfig } from './config';
import {
  aiCohortsColorsMap,
  aiCohortsFillColorsMap,
  aiCohortsMap,
} from '../../../configs/constants';
import { advancedPartialFilters } from '../../../configs/chart-config';
import { buildAdvancedLegendItems } from '../../../../_shared/utils/build-advanced-legend-items';
import { AI_COHORTS_TOOLTIPS } from '../../../../../../utils/advanced-metrics';

const emptyObject = {};
const cohortOrder = Object.keys(aiCohortsMap);

const renderCohortTooltip = (item) => (
  <div key={item.id}>
    <h6 className="h6 text-bold">{aiCohortsMap[item.id]}</h6>
    <p>{AI_COHORTS_TOOLTIPS[item.id]}</p>
  </div>
);

const UsageTrendChart = ({ isPending, series = emptyObject, isPress }) => {
  const chartRef = useRef(null);

  const { activeItem, hoveredItem, onClick, onMouseOver, onMouseOut } = useAdvancedLegend();
  const hoveredItemClassName = useHoveredItemClassName(series, hoveredItem, cohortOrder);
  useToggleSeriesVisibility(chartRef, activeItem);

  const metaSeriesData = useMemo(
    () =>
      cohortOrder.reduce(
        (acc, cohort) => ({
          ...acc,
          ...(series[cohort]
            ? {
                [cohort]: {
                  type: 'areaspline',
                  color: aiCohortsColorsMap[cohort],
                  fillColor: aiCohortsFillColorsMap[cohort],
                  usePassedFillColor: true,
                  name: aiCohortsMap[cohort],
                  id: cohort,
                  // manage visible via useToggleSeriesVisibility to preserve built-in animation
                },
              }
            : {}),
        }),
        {}
      ),
    [series]
  );

  const chartConfig = useMemo(
    () => getChartConfig({ onClick, onMouseOver, onMouseOut, isPress }),
    [onClick, onMouseOver, onMouseOut, isPress]
  );

  const legendOptions = useMemo(
    () =>
      buildAdvancedLegendItems(Object.values(metaSeriesData), {
        getTooltipContent: renderCohortTooltip,
      }),
    [metaSeriesData]
  );

  return isPending ? (
    <Skeleton height={272} className="d-flex" />
  ) : (
    <div className={cx('chart-container', hoveredItemClassName)}>
      <SmartChart
        ref={chartRef}
        chartId="app-impact-chart"
        series={series}
        metaSeriesData={metaSeriesData}
        config={chartConfig}
        allowPartialPeriods
        filters={advancedPartialFilters}
      />
      <AdvancedChartLegend
        items={legendOptions}
        activeItem={activeItem?.id || hoveredItem?.id}
        onClick={onClick}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
      />
    </div>
  );
};

UsageTrendChart.propTypes = {
  isPending: T.bool.isRequired,
  series: T.object,
  isPress: T.bool,
};

export default UsageTrendChart;
